import React, {FC} from 'react';
import styled, {useTheme} from 'styled-components';
import {DefaultTheme} from 'styled-components/dist/types';

import {hoverSelector} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {SetupGuideResourceTaskResource} from './setupGuideTypes';

/*
 * Props.
 */

interface SetupGuideFrontLineUserResourcesProps {
  resources: ReadonlyArray<SetupGuideResourceTaskResource>;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StyledArticle = styled(ChromelessButton)`
  display: grid;
  grid-template-areas: 'thumbnail text';
  grid-template-columns: 56px auto;
  padding: 8px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  border-radius: 16px;
  width: 388px;
  height: 90px;
  align-items: center;
  overflow: hidden;
  line-height: 18px;
`;

const StyledArticleText = styled.div`
  grid-area: text;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
`;

const StyledArticleTitle = styled.span`
  grid-area: title;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveText};
  }
`;

const StyledArticleDescription = styled.span`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  padding-top: 2px;
`;

const StyledIconSpan = styled.span`
  display: inline;
  white-space: nowrap;
`;

const StyledArticleLinkIcon = styled(Icon)`
  display: inline-block;
  color: transparent;
  margin-bottom: -3px;
  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveText};
  }
`;

const StyledArticleThumbnail = styled.div`
  grid-area: thumbnail;
  height: 74px;
`;

const StyledSquareItemDiv = styled.div<{
  $outerBackgroundColor: string;
}>`
  ${center()}
  height: 74px;
  width: 46px;
  background: ${(p) => p.$outerBackgroundColor};
  border-radius: 8px;
`;

const StyledCircleItemDiv = styled.div<{
  $innerBackgroundColor: string;
}>`
  height: 30px;
  width: 30px;
  background: ${(p) => p.$innerBackgroundColor};
  border-radius: 50%;
  ${center()}
`;

/*
 * Component.
 */

export const SetupGuideFrontLineUserResources: FC<SetupGuideFrontLineUserResourcesProps> = (props) => {
  const {resources} = props;
  const theme = useTheme();
  const {trackDataEvent} = useDataEventTracking();

  const makeOnClick = (resource: SetupGuideResourceTaskResource) => () => {
    const {link, displayName} = resource;

    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_GET_MORE_LMS_COURSE,
      {
        url: link,
        display_name: displayName,
      },
    );

    openInBrowser(link);
  };

  return (
    <StyledContainerDiv>
      {resources.map((resource) => (
        <StyledArticle key={resource.key} onClick={makeOnClick(resource)}>
          <StyledArticleThumbnail>{renderThumbnail(theme, resource)}</StyledArticleThumbnail>
          <StyledArticleText>
            <StyledArticleTitle>
              {resource.title}
              <StyledIconSpan>
                &nbsp;
                <StyledArticleLinkIcon name="externalLinkSmall" />
              </StyledIconSpan>

              <br />
            </StyledArticleTitle>
            <StyledArticleDescription>{resource.description}</StyledArticleDescription>
          </StyledArticleText>
        </StyledArticle>
      ))}
    </StyledContainerDiv>
  );
};

function renderThumbnail(theme: DefaultTheme, resource: SetupGuideResourceTaskResource) {
  const {icon, color} = resource;
  const {innerBackgroundColor, outerBackgroundColor} = computeThumbnailColors(theme, color);
  return (
    <StyledSquareItemDiv $outerBackgroundColor={outerBackgroundColor}>
      <StyledCircleItemDiv $innerBackgroundColor={innerBackgroundColor}>
        <Icon name={icon} color={theme.greys.white} />
      </StyledCircleItemDiv>
    </StyledSquareItemDiv>
  );
}

function computeThumbnailColors(theme: DefaultTheme, color: PaletteColorsEnum | undefined) {
  if (!color) {
    return {
      innerBackgroundColor: theme.palette.purple.shade40,
      outerBackgroundColor: theme.palette.purple.shade20,
    };
  }

  return {
    innerBackgroundColor: theme.palette[color].shade40,
    outerBackgroundColor: theme.palette[color].shade20,
  };
}
